import {getColorStyle} from '@/utilities/color-style-map';
import {getThemeColor} from '@/utilities/getThemeColor';
import classNames from 'classnames';
import {ComponentChildren} from 'preact';
import tinycolor from 'tinycolor2';

export type PackSelectorButtonProps = {
  badge?: string;
  badgeColor?: string;
  bottom?: ComponentChildren;
  color?: string;
  dataTestId?: string;
  isSelected?: boolean;
  middle?: ComponentChildren;
  onClick?: () => void;
  top?: ComponentChildren;
};

/**
 * A button for selecting pack sizes.
 * It can take a badge to display on the top of the button.
 * Badge text color will be automatically adjusted dark or light
 * based on the badge color to ensure readability.
 *
 * @param badge - The badge to display on the top of the button.
 * @param badgeColor - The color of the badge.
 * @param bottom - The text to display at the bottom of the button.
 * @param color - The color of the button.
 * @param dataTestId - The data-testid attribute value.
 * @param isSelected - Whether the button is selected.
 * @param middle - The text to display in the middle of the button.
 * @param onClick - The click event handler.
 * @param top - The text to display at the top of the button.
 * @returns The PackSelectorButton component.
 */
export default function PackSelectorButton({
  badge = '',
  isSelected = false,
  middle = null,
  onClick,
  top = null,
  dataTestId = '',
  color = 'primary-blue',
  badgeColor = 'primary-blue',
  bottom = null,
}: PackSelectorButtonProps) {
  const colorStyle = getColorStyle(color);
  const badgeColorStyle = getColorStyle(badgeColor);

  // Test whether the badge color is light or dark
  // This allows us to switch the text color to ensure readability
  const badgeColorHex = getThemeColor(badgeColor);
  const isLightBadge = badgeColorHex
    ? tinycolor(badgeColorHex).isLight()
    : false;

  return (
    <button
      data-testid={dataTestId}
      className={classNames(
        'fast-transition relative flex h-max w-full flex-col items-center justify-center rounded-b-xl p-2',
        {
          [`border-2 ${colorStyle.border} ${colorStyle.backgroundOpacity12}`]:
            isSelected,
          'border border-deep-ocean-blue/70': !isSelected,
          'rounded-t-xl': !badge,
        }
      )}
      onClick={onClick}
    >
      {!!badge && (
        <span
          className={classNames(
            'fast-transition  absolute top-0 z-10 -translate-y-full whitespace-nowrap rounded-t border px-2 py-0.5 text-[7px] font-medium leading-normal',
            badgeColorStyle.background,
            {
              'text-deep-ocean-blue': isLightBadge,
              'text-crisp-white': !isLightBadge,
              '-inset-x-[1px] border border-deep-ocean-blue/70': !isSelected,
              [`-inset-x-0.5 border-2 ${colorStyle.border}`]: isSelected,
            }
          )}
        >
          {badge}
        </span>
      )}{' '}
      <div className="flex flex-col">
        <span className="text-2xs leading-normal">{top}</span>
        {!!middle && (
          <span className="font-left21 text-2.5xl font-medium">{middle}</span>
        )}
      </div>
      {!!bottom && <span className="text-sm font-medium">{bottom}</span>}
    </button>
  );
}
